// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { Key, Check, Lock, ChevronDown, ChevronRight } from 'lucide-react'
import * as api from '../../api/client'

/**
 * Admin card to provision Owl Consultations' OWN Anthropic key for the report-builder AI
 * (structure-only translation, no PHI). Writes to Key Vault via the backend; the value is
 * write-only and never read back — only the last 4 and the source label are shown. Separate
 * from the per-pharmacy CareRx keys.
 */
export default function ReportAiKeyCard() {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState<api.ReportAiStatus | null>(null)
  const [keyInput, setKeyInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  const refresh = () => api.getReportAiStatus().then(setStatus).catch(() => setStatus(null))
  useEffect(() => { refresh() }, [])

  const fmt = (e: any) => {
    const code = e?.body?.error
    if (code === 'invalid_key') return "That doesn't look like an Anthropic key (it should start with 'sk-ant-')."
    if (code === 'key_vault_unavailable') return "Key Vault isn't reachable, or the app's managed identity lacks Set permission. " + (e?.body?.message ?? '')
    if (e?.status === 401 || e?.status === 403) return 'Admin only — your session may have expired.'
    return e?.body?.message ?? e?.message ?? 'Something went wrong.'
  }

  const save = async () => {
    const k = keyInput.trim(); if (!k) return
    setBusy(true); setMsg(null)
    try {
      const r = await api.setReportAiKey(k)
      setKeyInput('')
      setMsg({ kind: 'ok', text: `Stored (…${r.key_last4}). ${r.note}` })
      await refresh()
    } catch (e) { setMsg({ kind: 'err', text: fmt(e) }) } finally { setBusy(false) }
  }
  const clear = async () => {
    if (!window.confirm('Remove the stored Owl report-builder AI key?')) return
    setBusy(true); setMsg(null)
    try { await api.clearReportAiKey(); setMsg({ kind: 'ok', text: 'Key removed. AI assist is off (keyword fallback remains).' }); await refresh() }
    catch (e) { setMsg({ kind: 'err', text: fmt(e) }) } finally { setBusy(false) }
  }

  const enabled = status?.ai_enabled
  return (
    <div style={card}>
      <button onClick={() => setOpen(o => !o)} style={head} aria-expanded={open}>
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <Key size={15} color="#0c8f9c" />
        <span style={{ fontWeight: 700, fontSize: 13 }}>AI assist key — Owl Consultations</span>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5,
          color: enabled ? '#1d8a5a' : '#8a6111' }}>
          {enabled ? <><Check size={13} /> configured</> : <><Lock size={12} /> not set</>}
        </span>
      </button>

      {open && (
        <div style={{ padding: '4px 14px 14px' }}>
          <p style={{ fontSize: 12, color: '#5d6b7b', margin: '4px 0 10px', lineHeight: 1.5 }}>
            This is Owl Consultations' own Anthropic key for translating plain-language report
            requests into a query spec. It is <b>structure-only — no PHI is ever sent to the model</b>,
            and it is separate from each pharmacy's CareRx key. The key is write-only: it's stored in
            Key Vault and never shown again.
          </p>

          <div style={{ fontSize: 11.5, color: '#5d6b7b', marginBottom: 10 }}>
            Status: <b>{status?.key_source ?? '—'}</b>
            {status && <> · stores to <code style={code}>{status.expected_keyvault_secret}</code></>}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input type="password" value={keyInput} onChange={e => setKeyInput(e.target.value)}
              placeholder="sk-ant-…" autoComplete="off" spellCheck={false}
              style={{ flex: 1, minWidth: 200, border: '1px solid #e4e9ef', borderRadius: 7, padding: '8px 10px', fontSize: 13, fontFamily: 'monospace' }} />
            <button onClick={save} disabled={busy || !keyInput.trim()} style={{ ...btn, opacity: (busy || !keyInput.trim()) ? 0.5 : 1 }}>
              {busy ? 'Saving…' : enabled ? 'Replace key' : 'Save key'}
            </button>
            {enabled && <button onClick={clear} disabled={busy} style={ghost}>Remove</button>}
          </div>

          {msg && (
            <div role={msg.kind === 'err' ? 'alert' : undefined} style={{
              marginTop: 10, fontSize: 12.5, borderRadius: 7, padding: '8px 11px',
              background: msg.kind === 'ok' ? '#eef8f2' : '#fbe9e9', color: msg.kind === 'ok' ? '#1d6e47' : '#a23333',
            }}>{msg.text}</div>
          )}

          <p style={{ fontSize: 11, color: '#8a98a6', marginTop: 10 }}>
            The report-builder AI sends only field names and your phrasing to the model — never PHI —
            so it runs under Owl's key, separate from each pharmacy's CareRx key. To set it as an app
            setting instead, use <code style={code}>{status?.expected_app_setting ?? 'OWL_REPORTS_ANTHROPIC_API_KEY'}</code>.
          </p>
        </div>
      )}
    </div>
  )
}

const card: React.CSSProperties = { background: '#fff', border: '1px solid #e4e9ef', borderRadius: 9, marginBottom: 14 }
const head: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 9, width: '100%', background: 'none', border: 0, padding: '12px 14px', cursor: 'pointer', textAlign: 'left' }
const btn: React.CSSProperties = { background: '#0c8f9c', color: '#fff', border: 0, padding: '8px 15px', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: 'pointer' }
const ghost: React.CSSProperties = { background: '#fff', border: '1px solid #e4e9ef', color: '#16212e', padding: '8px 13px', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: 'pointer' }
const code: React.CSSProperties = { background: '#f1f4f7', padding: '1px 5px', borderRadius: 4, fontSize: 11 }
